import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, ArrowLeft } from 'lucide-react';
import { useAppSelector } from '../store/hooks';
import type { BlogPost } from '../types';
import { blogPosts, BLOG_POSTS_COUNT } from '../data/blogPosts';
import './BlogPage.css';

const BlogPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { postId } = useParams<{ postId?: string }>();
  const { isAuthenticated } = useAppSelector((state) => state.user);

  const isBlogActive = location.pathname.startsWith('/blog');

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  const filteredPosts = [...blogPosts].sort(
    (a, b) => new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime()
  );

  const selectedPost = postId ? blogPosts.find(post => post.id === postId) : null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Generate Article Schema for SEO
  const generateArticleSchema = (post: BlogPost) => {
    const baseUrl = window.location.origin;
    const slug = post.id;
    const articleUrl = `${baseUrl}/blog/${slug}`;
    
    // Format date to ISO 8601 format (YYYY-MM-DD)
    const publishedDate = post.publishedDate;
    
    const schema: any = {
      "@context": "https://schema.org",
      "@type": "BlogPosting", // Using BlogPosting instead of Article for blog posts
      "headline": post.title,
      "description": post.excerpt,
      "author": {
        "@type": "Person",
        "name": post.author
      },
      "publisher": {
        "@type": "Organization",
        "name": "Focuzon",
        "logo": {
          "@type": "ImageObject",
          "url": `${baseUrl}/focuzon.png`
        }
      },
      "datePublished": publishedDate,
      "dateModified": publishedDate,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": articleUrl
      }
    };

    // Add image if available (as array for better SEO)
    if (post.image) {
      schema.image = [
        {
          "@type": "ImageObject",
          "url": post.image.startsWith('http') ? post.image : `${baseUrl}${post.image}`
        }
      ];
    }

    return schema;
  };

  // Generate FAQ Schema (only include if there are actual FAQs)
  const generateFAQSchema = (post: BlogPost) => {
    if (post.faqs && post.faqs.length > 0) {
      return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": post.faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      };
    }
    return null;
  };

  // Generate Breadcrumb Schema for SEO
  const generateBreadcrumbSchema = (post: BlogPost) => {
    const baseUrl = window.location.origin;
    const articleUrl = `${baseUrl}/blog/${post.id}`;
    
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": `${baseUrl}/`
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Blog",
          "item": `${baseUrl}/blog`
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": post.title,
          "item": articleUrl
        }
      ]
    };
  };

  if (selectedPost) {
    const articleSchema = generateArticleSchema(selectedPost);
    const faqSchema = generateFAQSchema(selectedPost);
    const breadcrumbSchema = generateBreadcrumbSchema(selectedPost);

    return (
      <>
        <Helmet>
          <title>{selectedPost.title} | Focuzon Blog</title>
          <meta name="description" content={selectedPost.excerpt} />
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
          {faqSchema && (
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
          )}
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
        </Helmet>
        <div className="min-h-screen bg-[#FFF8F0] relative overflow-x-hidden">
          {/* Background gradient shapes */}
          <div className="fixed inset-0 pointer-events-none z-0">
            <div className="absolute top-0 -left-1/4 w-96 h-96 bg-gradient-to-br from-orange-300/50 to-orange-200/40 rounded-full blur-3xl"></div>
            <div className="absolute top-1/4 -right-1/4 w-96 h-96 bg-gradient-to-br from-orange-400/50 to-orange-300/40 rounded-full blur-3xl"></div>
          </div>

          {/* Navigation - Fixed Header */}
          <nav className="fixed top-0 left-0 right-0 z-50 border-b border-orange-100 bg-[#FFF8F0]/80 backdrop-blur-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between h-16">
                {/* Logo - Left Aligned */}
                <div className="flex items-center justify-center flex-shrink-0 h-full">
                  <img 
                    src="/focuzon.png" 
                    alt="Focuzon Logo" 
                    className="h-8 w-auto object-contain cursor-pointer hover:opacity-90 transition-opacity"
                    style={{ maxWidth: '140px', minWidth: '100px' }}
                    onClick={() => navigate('/')}
                  />
                </div>

                {/* Navigation Menu - Centered */}
                <div className="hidden lg:flex items-center justify-center flex-1">
                  <div className="flex items-center space-x-1 bg-white/60 backdrop-blur-sm rounded-full px-2 py-1.5 border border-orange-100/50 shadow-sm">
                    <button
                      onClick={() => navigate('/')}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                        location.pathname === '/'
                          ? 'text-orange-600 bg-orange-50 shadow-sm'
                          : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50/50'
                      }`}
                    >
                      Home
                    </button>
                    <div className="w-px h-4 bg-orange-200/50"></div>
                    <button
                      onClick={() => navigate('/blog')}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                        isBlogActive
                          ? 'text-orange-600 bg-orange-50 shadow-sm'
                          : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50/50'
                      }`}
                    >
                      Blog
                    </button>
                    <div className="w-px h-4 bg-orange-200/50"></div>
                    <button
                      onClick={() => navigate('/about')}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                        location.pathname === '/about'
                          ? 'text-orange-600 bg-orange-50 shadow-sm'
                          : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50/50'
                      }`}
                    >
                      About Us
                    </button>
                  </div>
                </div>

                {/* Auth Buttons - Right Aligned */}
                <div className="flex items-center space-x-3 flex-shrink-0">
                  {!isAuthenticated ? (
                    <>
                      <button 
                        onClick={() => navigate('/login')}
                        className="hidden sm:block text-gray-700 hover:text-orange-600 font-medium transition-colors text-sm px-3 py-1.5 rounded-lg hover:bg-orange-50/50"
                      >
                        Log in
                      </button>
                      <button 
                        onClick={() => navigate('/signup')}
                        className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-5 py-2 rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg shadow-orange-200 text-sm whitespace-nowrap"
                      >
                        Sign up
                      </button>
                    </>
                  ) : (
                    <button 
                      onClick={goToDashboard}
                      className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-5 py-2 rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg shadow-orange-200 text-sm whitespace-nowrap"
                    >
                      Go to Dashboard
                    </button>
                  )}
                </div>
              </div>
            </div>
          </nav>

          {/* Blog Post Content - Add padding-top for fixed header */}
          <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12 relative z-10">
            {/* Back Button */}
            <button
              onClick={() => navigate('/blog')}
              className="flex items-center gap-2 text-gray-600 hover:text-orange-600 mb-6 transition-colors group"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              <span className="font-medium">Back to Blog</span>
            </button>
            
            <article className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-orange-200/50 shadow-lg">
              {/* Header */}
              <div className="mb-8">
                <div className="flex items-center gap-4 mb-4">
                  <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                    {selectedPost.category}
                  </span>
                  {selectedPost.tags.map(tag => (
                    <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  {selectedPost.title}
                </h1>
                <div className="flex flex-wrap items-center gap-6 text-gray-600">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    <span>{formatDate(selectedPost.publishedDate)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    <span>{selectedPost.readTime} min read</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div 
                className="blog-content text-gray-700"
                dangerouslySetInnerHTML={{ __html: selectedPost.content }}
              />
            </article>

            {/* Related Posts */}
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">More Articles</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {blogPosts
                  .filter(post => post.id !== selectedPost.id)
                  .slice(0, 2)
                  .map(post => (
                    <div
                      key={post.id}
                      onClick={() => navigate(`/blog/${post.id}`)}
                      className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-orange-200/50 hover:shadow-lg cursor-pointer transition-all"
                    >
                      <span className="text-sm text-orange-600 font-medium">{post.category}</span>
                      <h3 className="text-xl font-semibold text-gray-900 mt-2 mb-2">{post.title}</h3>
                      <p className="text-gray-600 text-sm mb-4">{post.excerpt}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{formatDate(post.publishedDate)}</span>
                        <span>•</span>
                        <span>{post.readTime} min read</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Blog | Focuzon - Productivity & Focus Insights</title>
        <meta name="description" content="Discover insights on productivity, focus, deep work, and mindfulness from the Focuzon team." />
      </Helmet>
      <div className="min-h-screen bg-[#FFF8F0] relative overflow-x-hidden">
        {/* Background gradient shapes */}
        <div className="fixed inset-0 pointer-events-none z-0">
          <div className="absolute top-0 -left-1/4 w-96 h-96 bg-gradient-to-br from-orange-300/50 to-orange-200/40 rounded-full blur-3xl"></div>
          <div className="absolute top-1/4 -right-1/4 w-96 h-96 bg-gradient-to-br from-orange-400/50 to-orange-300/40 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-gradient-to-br from-orange-200/40 to-orange-100/30 rounded-full blur-3xl"></div>
        </div>

        {/* Navigation - Fixed Header */}
        <nav className="fixed top-0 left-0 right-0 z-50 border-b border-orange-100 bg-[#FFF8F0]/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Logo - Left Aligned */}
              <div className="flex items-center justify-center flex-shrink-0 h-full">
                <img 
                  src="/focuzon.png" 
                  alt="Focuzon Logo" 
                  className="h-8 w-auto object-contain cursor-pointer hover:opacity-90 transition-opacity"
                  style={{ maxWidth: '140px', minWidth: '100px' }}
                  onClick={() => navigate('/')}
                />
              </div>

              {/* Navigation Menu - Centered */}
              <div className="hidden lg:flex items-center justify-center flex-1">
                <div className="flex items-center space-x-1 bg-white/60 backdrop-blur-sm rounded-full px-2 py-1.5 border border-orange-100/50 shadow-sm">
                  <button
                    onClick={() => navigate('/')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                      location.pathname === '/'
                        ? 'text-orange-600 bg-orange-50 shadow-sm'
                        : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50/50'
                    }`}
                  >
                    Home
                  </button>
                  <div className="w-px h-4 bg-orange-200/50"></div>
                  <button
                    onClick={() => navigate('/blog')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                      isBlogActive
                        ? 'text-orange-600 bg-orange-50 shadow-sm'
                        : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50/50'
                    }`}
                  >
                    Blog
                  </button>
                  <div className="w-px h-4 bg-orange-200/50"></div>
                  <button
                    onClick={() => navigate('/about')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                      location.pathname === '/about'
                        ? 'text-orange-600 bg-orange-50 shadow-sm'
                        : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50/50'
                    }`}
                  >
                    About Us
                  </button>
                </div>
              </div>

              {/* Auth Buttons - Right Aligned */}
              <div className="flex items-center space-x-3 flex-shrink-0">
                {!isAuthenticated ? (
                  <>
                    <button 
                      onClick={() => navigate('/login')}
                      className="hidden sm:block text-gray-700 hover:text-orange-600 font-medium transition-colors text-sm px-3 py-1.5 rounded-lg hover:bg-orange-50/50"
                    >
                      Log in
                    </button>
                    <button 
                      onClick={() => navigate('/signup')}
                      className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-5 py-2 rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg shadow-orange-200 text-sm whitespace-nowrap"
                    >
                      Sign up
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={goToDashboard}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-5 py-2 rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg shadow-orange-200 text-sm whitespace-nowrap"
                  >
                    Go to Dashboard
                  </button>
                )}
              </div>
            </div>
          </div>
        </nav>

        {/* Blog Content - Add padding-top for fixed header */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Focuzon Blog
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Insights on productivity, focus, deep work, and building better work habits.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Showing all {BLOG_POSTS_COUNT} articles
            </p>
          </div>

          {/* Blog Posts Grid - all posts visible, equal card heights */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filteredPosts.map(post => (
              <div
                key={post.id}
                onClick={() => navigate(`/blog/${post.id}`)}
                className="flex flex-col bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-orange-200/50 hover:shadow-xl cursor-pointer transition-all hover:-translate-y-1 min-h-[280px]"
              >
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                    {post.category}
                  </span>
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 line-clamp-2 flex-shrink-0">
                  {post.title}
                </h2>
                <p className="text-gray-600 mb-4 line-clamp-3 flex-1 min-h-0">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t border-orange-100 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 flex-shrink-0" />
                    <span>{formatDate(post.publishedDate)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 flex-shrink-0" />
                    <span>{post.readTime} min</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredPosts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No posts found in this category.</p>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default BlogPage;
